import DynamicSelect from "@/shared/components/DynamicSelect/DynamicSelect.vue";
import Snackbar from "@/shared/components/Snackbar/Snackbar.vue";
import {
  FORM_MESSAGES,
  LIST_DATA_EVENTS
} from "@/shared/constants/FormConstants";
import {
  GENERAL_CONSTANTS,
  PANEL_INFO_CONSTANTS,
  STORE_CONSTANTS
} from "@/shared/constants/GeneralConstants";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import { MenuDataStructure } from "@/shared/models/MenuDataStructure";
import { Response } from "@/shared/models/Response";
import { SearchContext } from "@/shared/models/SearchContext";
import store from "@/store";
import { Http } from "@status/codes";
import { Component, Inject, Vue, Watch } from "vue-property-decorator";
import { mapGetters } from "vuex";
import { USER_ACCESS } from "../User/constants/UserConstants";
import SaveDashboardTemplate from "./components/SaveDashboardTemplate/SaveDashboardTemplate.vue";
import {
  DASHBOARD_TEMPLATE_FILTERS,
  DASHBOARD_TEMPLATE_MESSAGES,
  DASHBOARD_TEMPLATES_TITLE
} from "./constants/DashboardTemplatesConstants";
import { DashboardTemplateListResponse } from "./models/DashboardTemplateListResponse";
import { BqDashboardService } from "./services/BqDashboardService";

@Component({
  name: "DashboardTemplates",
  components: { DynamicSelect, SaveDashboardTemplate },
  computed: {
    ...mapGetters({
      dataToMainView: "getDataToMainView",
      showLoadingBar: "getShowLoadingBar"
    })
  }
})
export default class DashboardTemplates extends Vue {
  @Inject() bqDashboardService!: BqDashboardService;

  dataToMainView!: MenuDataStructure;
  showLoadingBar!: boolean;
  primaryBtnClassRound = STYLE_CLASSES.PRIMARY_ROUND_BTN;
  currentIcon = GENERAL_CONSTANTS.ICONS.ADD;
  currentTooltip = DASHBOARD_TEMPLATE_MESSAGES.CREATE;
  showAddDashboardTemplate = false;
  filterArray = DASHBOARD_TEMPLATE_FILTERS.MAIN;
  OPERATIONS = [
    LIST_DATA_EVENTS.DELETE_DATA,
    LIST_DATA_EVENTS.EDIT_DATA,
    LIST_DATA_EVENTS.DUPLICATE_DATA
  ];
  loadingData = false;
  dashboardtemplatesList!: Response<DashboardTemplateListResponse[]>;
  itemList: Array<DashboardTemplateListResponse> = [];
  selectedDashboardTemplateId = "";

  async mounted() {
    await this.searchDashboardTemplates(true);
    await this.emitSearchInitializer();
  }

  async emitSearchInitializer() {
    await this.initializeFilters();
    const searchContext: SearchContext = {
      title: DASHBOARD_TEMPLATES_TITLE,
      filterArray: [this.filterArray],
      operations: this.OPERATIONS,
      clickAction: LIST_DATA_EVENTS.EDIT_DATA,
      callbackHandleOperation: this.handleOperation,
      descriptions: ["name", "state"]
    };
    const data = searchContext;
    this.sendDataToLateralMenu(
      PANEL_INFO_CONSTANTS.ACTION.SET_SEARCH_CONTEXT,
      data
    );
  }

  @Watch("dataToMainView")
  handleDataToMainView() {
    const validations: Record<string, void> = {
      Panel: this.panelValidation()
    };

    validations[this.dataToMainView.from];
  }

  panelValidation() {
    if (this.dataToMainView.type === PANEL_INFO_CONSTANTS.TYPE.SEARCH) {
      switch (this.dataToMainView.action) {
        case PANEL_INFO_CONSTANTS.ACTION.DO_SEARCH_TEMPLATE: {
          if (!this.dataToMainView.data.name) {
            this.sendDataToLateralMenu(
              PANEL_INFO_CONSTANTS.ACTION.SET_LIST_ITEM_CONTEXT,
              this.itemList
            );
            return;
          }

          const items = this.itemList.filter(p =>
            p.name
              .toLowerCase()
              .includes(this.dataToMainView.data.name[0].toLowerCase())
          );
          this.sendDataToLateralMenu(
            PANEL_INFO_CONSTANTS.ACTION.SET_LIST_ITEM_CONTEXT,
            items
          );
          break;
        }
      }
    }
  }

  async initializeFilters() {
    const secondaryFilters = [];
    secondaryFilters.push(DASHBOARD_TEMPLATE_FILTERS.SECONDARY.NAME);
    this.filterArray.secondaryFilters = secondaryFilters;
  }

  sendDataToLateralMenu(action: string, data?: any) {
    store.commit(STORE_CONSTANTS.TO_LATERAL_MENU, {
      from: PANEL_INFO_CONSTANTS.FROM.CRUD,
      type: PANEL_INFO_CONSTANTS.TYPE.SEARCH,
      action: action,
      data: data
    });
  }

  async handleOperation(action: string, itemId: string) {
    switch (action) {
      case LIST_DATA_EVENTS.DELETE_DATA:
        this.deleteTemplate(itemId);
        break;
      case LIST_DATA_EVENTS.EDIT_DATA:
        this.editDashboardTemplate(itemId);
        break;
      case LIST_DATA_EVENTS.DUPLICATE_DATA:
        this.duplicateTemplate(itemId);
        break;
    }
  }

  async duplicateTemplate(itemId: string) {
    try {
      const response = await this.bqDashboardService.duplicateTemplateById(
        itemId
      );
      if (response.code === Http.Ok) {
        Snackbar.popSuccess(response.message);
        await this.searchDashboardTemplates(true);
      } else {
        Snackbar.popError(response.message);
      }
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async deleteTemplate(itemId: string) {
    try {
      await this.bqDashboardService.deleteDashboardTemplate(itemId);
      await this.searchDashboardTemplates(true);

      if (this.selectedDashboardTemplateId === itemId) {
        this.showAddDashboardTemplate = false;
        this.currentIcon = GENERAL_CONSTANTS.ICONS.ADD;
        this.currentTooltip = DASHBOARD_TEMPLATE_MESSAGES.CREATE;
      }
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async showSaveDashboardTemplate(template?: DashboardTemplateListResponse) {
    this.showAddDashboardTemplate = !this.showAddDashboardTemplate;
    this.currentIcon = this.showAddDashboardTemplate
      ? GENERAL_CONSTANTS.ICONS.CANCEL
      : GENERAL_CONSTANTS.ICONS.ADD;
    this.currentTooltip = this.showAddDashboardTemplate
      ? DASHBOARD_TEMPLATE_MESSAGES.CANCEL
      : DASHBOARD_TEMPLATE_MESSAGES.CREATE;

    if (template) {
      await this.sendDataToLateralMenu(
        PANEL_INFO_CONSTANTS.ACTION.UNDO_SELECTED_ITEM
      );
      this.searchDashboardTemplates(true, template.id);
      return;
    }
    this.sendDataToLateralMenu(
      PANEL_INFO_CONSTANTS.ACTION.SET_SELECTED_ITEM_NULL
    );
    this.selectedDashboardTemplateId = "";
  }

  async searchDashboardTemplates(shouldSearch: boolean, id?: string) {
    if (!shouldSearch) return;
    this.loadingData = true;
    this.sendDataToLateralMenu(
      PANEL_INFO_CONSTANTS.ACTION.LOADING_CONTEXT_AND_HIDE_CARDS
    );

    this.itemList = [];
    this.dashboardtemplatesList = await this.getData();
    if (
      this.dashboardtemplatesList.code === Http.Found &&
      this.dashboardtemplatesList.data !== undefined
    ) {
      const newList = [...this.dashboardtemplatesList.data];
      const idSelect = id ? id : this.selectedDashboardTemplateId;

      newList.sort((a, b) => {
        if (a.id === idSelect) return -1;
        if (b.id === idSelect) return 1;
        return 0;
      });

      this.itemList = newList;
      this.sendDataToLateralMenu(
        PANEL_INFO_CONSTANTS.ACTION.SET_LIST_ITEM_CONTEXT,
        this.itemList
      );
    } else {
      this.sendDataToLateralMenu(PANEL_INFO_CONSTANTS.ACTION.LOADED_CONTEXT);
      if (this.dashboardtemplatesList.code === Http.NotFound) {
        Snackbar.popInfo(this.dashboardtemplatesList.message);
      } else {
        Snackbar.popError(this.dashboardtemplatesList.message);
      }
    }

    this.loadingData = false;
  }

  async getData(): Promise<Response<DashboardTemplateListResponse[]>> {
    try {
      const response = await this.bqDashboardService.getAllTemplates();
      return response;
    } catch (error) {
      let responseError: Response<DashboardTemplateListResponse[]>;
      if ((error as Error).message === GENERAL_CONSTANTS.FORBIDDEN) {
        responseError = {
          code: Http.Forbidden,
          message: FORM_MESSAGES.FORBIDDEN_REQUEST + USER_ACCESS.GET
        };
      } else {
        responseError = {
          code: Http.BadRequest,
          message: FORM_MESSAGES.REQUEST_FAILED
        };
      }
      return responseError;
    }
  }

  editDashboardTemplate(id: string) {
    this.sendDataToLateralMenu(
      PANEL_INFO_CONSTANTS.ACTION.SET_SELECTED_ITEM,
      id
    );
    this.showAddDashboardTemplate = true;
    this.currentIcon = GENERAL_CONSTANTS.ICONS.CANCEL;
    this.currentTooltip = DASHBOARD_TEMPLATE_MESSAGES.CANCEL;
    this.selectedDashboardTemplateId = id;
  }
}
